import { API_CONFIG, API_ENDPOINTS } from '../config/api.config';
import authService from '../auth/authService';
import { apiGet } from '../utils/apiClient';
import type {
  CriticalResultsAlertCategory,
  CriticalResultsDashboardAlert,
  CriticalResultsDashboardFilters,
  CriticalResultsDashboardSummary,
  CriticalResultsEscalationSummary,
  CriticalResultsExportDataset,
  CriticalResultsExportFormat,
  CriticalResultsListParams,
} from '../types/criticalResultsDashboard.types';

interface ApiEnvelope<T> {
  success?: boolean;
  message?: string;
  count?: number;
  data?: T;
  errors?: unknown[];
}

function buildQueryString(params: Record<string, string | number | undefined>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || String(value).trim() === '') {
      return;
    }
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

function mapSummary(raw: Record<string, unknown>): CriticalResultsDashboardSummary {
  return {
    openAlerts: Number(raw.openAlerts ?? raw.open_alerts ?? 0),
    pendingAcknowledgements: Number(
      raw.pendingAcknowledgements ?? raw.pending_acknowledgements ?? 0
    ),
    acknowledgedAlerts: Number(raw.acknowledgedAlerts ?? raw.acknowledged_alerts ?? 0),
    escalatedAlerts: Number(raw.escalatedAlerts ?? raw.escalated_alerts ?? 0),
    slaBreaches: Number(raw.slaBreaches ?? raw.sla_breaches ?? 0),
    withinSlaAlerts: Number(raw.withinSlaAlerts ?? raw.within_sla_alerts ?? 0),
    cancelledAlerts: Number(raw.cancelledAlerts ?? raw.cancelled_alerts ?? 0),
    averageTatSeconds: Number(raw.averageTatSeconds ?? raw.average_tat_seconds ?? 0),
    medianTatSeconds: Number(raw.medianTatSeconds ?? raw.median_tat_seconds ?? 0),
    criticalitySummary: (Array.isArray(raw.criticalitySummary ?? raw.criticality_summary)
      ? (raw.criticalitySummary ?? raw.criticality_summary)
      : []
    ).map((item: Record<string, unknown>) => ({
      criticalityBand: String(item.criticalityBand ?? item.criticality_band ?? ''),
      total: Number(item.total ?? item.count ?? 0),
      open: Number(item.open ?? 0),
      acknowledged: Number(item.acknowledged ?? 0),
      breaches: Number(item.breaches ?? 0),
      averageTatSeconds: Number(item.averageTatSeconds ?? item.average_tat_seconds ?? 0),
    })),
    departmentSummary: (Array.isArray(raw.departmentSummary ?? raw.department_summary)
      ? (raw.departmentSummary ?? raw.department_summary)
      : []
    ).map((item: Record<string, unknown>) => ({
      department: String(item.department ?? 'Unassigned'),
      count: Number(item.count ?? 0),
    })),
    facilitySummary: (Array.isArray(raw.facilitySummary ?? raw.facility_summary)
      ? (raw.facilitySummary ?? raw.facility_summary)
      : []
    ).map((item: Record<string, unknown>) => ({
      facilityId: Number(item.facilityId ?? item.facility_id ?? 0),
      facilityName: String(item.facilityName ?? item.facility_name ?? ''),
      facilityCode: (item.facilityCode ?? item.facility_code ?? null) as string | null,
      count: Number(item.count ?? 0),
    })),
    referringClinicianSummary: (Array.isArray(
      raw.referringClinicianSummary ?? raw.referring_clinician_summary
    )
      ? (raw.referringClinicianSummary ?? raw.referring_clinician_summary)
      : []
    ).map((item: Record<string, unknown>) => {
      let name = '';
      if (typeof item.name === 'string') {
        name = item.name;
      } else if (typeof item.name === 'number') {
        name = String(item.name);
      }
      return {
        name,
        count: Number(item.count ?? 0),
      };
    }),
  };
}

function mapDisplayStatusCounts(raw: Record<string, unknown> = {}) {
  return {
    pending: Number(raw.pending ?? 0),
    escalating: Number(raw.escalating ?? 0),
    breached: Number(raw.breached ?? 0),
    acknowledged: Number(raw.acknowledged ?? 0),
    closed: Number(raw.closed ?? 0),
  };
}

function mapEscalationSummary(raw: Record<string, unknown>): CriticalResultsEscalationSummary {
  return {
    byLevel: (Array.isArray(raw.byLevel ?? raw.by_level) ? (raw.byLevel ?? raw.by_level) : []).map(
      (item: Record<string, unknown>) => ({
        levelNumber: Number(item.levelNumber ?? item.level_number ?? 0),
        count: Number(item.count ?? 0),
      })
    ),
    byStatus: (Array.isArray(raw.byStatus ?? raw.by_status)
      ? (raw.byStatus ?? raw.by_status)
      : []
    ).map((item: Record<string, unknown>) => ({
      status: String(item.status ?? ''),
      count: Number(item.count ?? 0),
    })),
    byDisplayStatus: mapDisplayStatusCounts(
      (raw.byDisplayStatus ?? raw.by_display_status) as Record<string, unknown>
    ),
    timeSeries: (Array.isArray(raw.timeSeries ?? raw.time_series)
      ? (raw.timeSeries ?? raw.time_series)
      : []
    ).map((item: Record<string, unknown>) => ({
      periodLabel: String(item.periodLabel ?? item.period_label ?? ''),
      periodStart: String(item.periodStart ?? item.period_start ?? ''),
      breaches: Number(item.breaches ?? 0),
      escalations: Number(item.escalations ?? 0),
    })),
  };
}

function mapAlert(raw: Record<string, unknown>): CriticalResultsDashboardAlert {
  const study = (raw.study as Record<string, unknown> | null | undefined) ?? null;
  return {
    id: Number(raw.id),
    escalationInstanceId: Number(raw.escalationInstanceId ?? raw.escalation_instance_id ?? raw.id),
    criticalNotificationId:
      raw.criticalNotificationId != null
        ? Number(raw.criticalNotificationId)
        : raw.critical_notification_id != null
          ? Number(raw.critical_notification_id)
          : null,
    notificationStatus: (raw.notificationStatus ?? raw.notification_status) as string | null,
    acknowledgementStatus: (raw.acknowledgementStatus ?? raw.acknowledgement_status) as
      | string
      | null,
    facilityId: Number(raw.facilityId ?? raw.facility_id),
    studyId: Number(raw.studyId ?? raw.study_id),
    criticalityBand: String(raw.criticalityBand ?? raw.criticality_band ?? ''),
    status: (raw.status as CriticalResultsDashboardAlert['status']) ?? 'Active',
    displayStatus: (raw.displayStatus ??
      raw.display_status ??
      'Pending') as CriticalResultsDashboardAlert['displayStatus'],
    pendingAcknowledgement: Boolean(
      raw.pendingAcknowledgement ?? raw.pending_acknowledgement ?? false
    ),
    currentLevelNumber: Number(raw.currentLevelNumber ?? raw.current_level_number ?? 1),
    department: (raw.department as string) ?? null,
    slaStatus: (raw.slaStatus ?? raw.sla_status) as string | undefined,
    timeRemainingSeconds: Number(raw.timeRemainingSeconds ?? raw.time_remaining_seconds ?? 0),
    acknowledgementTatSeconds:
      raw.acknowledgementTatSeconds != null
        ? Number(raw.acknowledgementTatSeconds)
        : raw.acknowledgement_tat_seconds != null
          ? Number(raw.acknowledgement_tat_seconds)
          : null,
    startedAt: String(raw.startedAt ?? raw.started_at ?? ''),
    acknowledgedAt: (raw.acknowledgedAt ?? raw.acknowledged_at) as string | null | undefined,
    study: study
      ? {
          accessionNumber: (study.accessionNumber ?? study.accession_number) as string | null,
          patientName: (study.patientName ?? study.patient_name) as string | null,
          patientUhid: (study.patientUhid ?? study.patient_uhid) as string | null,
          risVisitId: (study.risVisitId ?? study.ris_visit_id) as string | null,
          testName: (study.testName ?? study.test_name) as string | null,
          modalityName: (study.modalityName ?? study.modality_name) as string | null,
          studyDisplayName: (study.studyDisplayName ?? study.study_display_name) as string | null,
          studyDescription: (study.studyDescription ?? study.study_description) as string | null,
          criticalReason: (study.criticalReason ?? study.critical_reason) as string | null,
          studyInstanceUid: (study.studyInstanceUid ?? study.study_instance_uid) as string | null,
          referredBy: (study.referredBy ?? study.referred_by) as string | null,
        }
      : null,
  };
}

function toFilterParams(filters: CriticalResultsDashboardFilters) {
  return {
    facilityId: filters.facilityId,
    criticalityBand: filters.criticalityBand,
    status: filters.status,
    escalationLevel: filters.escalationLevel,
    department: filters.department,
    referringClinician: filters.referringClinician,
    startedAfter: filters.startedAfter,
    startedBefore: filters.startedBefore,
    search: filters.search,
  };
}

const ALERT_LIST_ENDPOINTS: Record<CriticalResultsAlertCategory, string> = {
  all: API_ENDPOINTS.CRITICAL_RESULTS_DASHBOARD.CRITICAL_ALERTS,
  open: API_ENDPOINTS.CRITICAL_RESULTS_DASHBOARD.OPEN_ALERTS,
  pendingAcknowledgement: API_ENDPOINTS.CRITICAL_RESULTS_DASHBOARD.PENDING_ACKNOWLEDGEMENTS,
  acknowledged: API_ENDPOINTS.CRITICAL_RESULTS_DASHBOARD.ACKNOWLEDGED_ALERTS,
  escalated: API_ENDPOINTS.CRITICAL_RESULTS_DASHBOARD.ESCALATED_ALERTS,
  slaBreached: API_ENDPOINTS.CRITICAL_RESULTS_DASHBOARD.SLA_BREACHES,
  withinSla: API_ENDPOINTS.CRITICAL_RESULTS_DASHBOARD.OPEN_ALERTS,
  cancelled: API_ENDPOINTS.CRITICAL_RESULTS_DASHBOARD.CANCELLED_ALERTS,
  deliveryFailed: API_ENDPOINTS.CRITICAL_RESULTS_DASHBOARD.DELIVERY_FAILED_ALERTS,
};

export async function getCriticalResultsSummary(
  filters: CriticalResultsDashboardFilters
): Promise<{ success: boolean; data?: CriticalResultsDashboardSummary; error?: string }> {
  try {
    const url = `${API_ENDPOINTS.CRITICAL_RESULTS_DASHBOARD.SUMMARY}${buildQueryString(toFilterParams(filters))}`;
    const response = await apiGet<ApiEnvelope<Record<string, unknown>>>(url, { requireAuth: true });
    const payload = response.data;
    if (payload?.success === false) {
      return { success: false, error: payload.message || 'Failed to load dashboard summary' };
    }
    const raw = payload?.data ?? (payload as unknown as Record<string, unknown>);
    return { success: true, data: mapSummary(raw) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to load dashboard summary',
    };
  }
}

export async function getCriticalResultsEscalationSummary(
  filters: CriticalResultsDashboardFilters
): Promise<{ success: boolean; data?: CriticalResultsEscalationSummary; error?: string }> {
  try {
    const url = `${API_ENDPOINTS.CRITICAL_RESULTS_DASHBOARD.ESCALATION_SUMMARY}${buildQueryString(toFilterParams(filters))}`;
    const response = await apiGet<ApiEnvelope<Record<string, unknown>>>(url, { requireAuth: true });
    const payload = response.data;
    if (payload?.success === false) {
      return { success: false, error: payload.message || 'Failed to load escalation summary' };
    }
    const raw = payload?.data ?? (payload as unknown as Record<string, unknown>);
    return { success: true, data: mapEscalationSummary(raw) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to load escalation summary',
    };
  }
}

export async function listCriticalResultsAlerts(
  category: CriticalResultsAlertCategory,
  params: CriticalResultsListParams
): Promise<{
  success: boolean;
  data: CriticalResultsDashboardAlert[];
  count: number;
  error?: string;
}> {
  try {
    const endpoint = ALERT_LIST_ENDPOINTS[category];
    const query = buildQueryString({
      ...toFilterParams(params),
      limit: params.limit,
      offset: params.offset,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
    });
    const response = await apiGet<ApiEnvelope<Record<string, unknown>[]>>(`${endpoint}${query}`, {
      requireAuth: true,
    });
    const payload = response.data;
    if (payload?.success === false) {
      return {
        success: false,
        data: [],
        count: 0,
        error: payload.message || 'Failed to load alerts',
      };
    }
    const rows = Array.isArray(payload?.data) ? payload.data.map(mapAlert) : [];
    return { success: true, data: rows, count: Number(payload?.count ?? rows.length) };
  } catch (error) {
    return {
      success: false,
      data: [],
      count: 0,
      error: error instanceof Error ? error.message : 'Failed to load alerts',
    };
  }
}

export async function exportCriticalResultsDashboard(
  dataset: CriticalResultsExportDataset,
  format: CriticalResultsExportFormat,
  params: CriticalResultsListParams
): Promise<void> {
  const url = `${API_ENDPOINTS.CRITICAL_RESULTS_DASHBOARD.EXPORT}${buildQueryString({
    ...toFilterParams(params),
    dataset,
    format,
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    search: params.search,
  })}`;
  const authHeader = authService.getAuthHeader();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      accept:
        'text/csv, application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json',
      'tenant-id': API_CONFIG.TENANT_ID,
      ...(authHeader.Authorization ? { Authorization: authHeader.Authorization } : {}),
    },
    mode: 'cors',
  });

  if (!response.ok) {
    let message = `Export failed (${response.status})`;
    try {
      const json = await response.json();
      message = json?.message || json?.error || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename =
    match?.[1] || `critical-results-${dataset}.${format === 'excel' ? 'xlsx' : format}`;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
